import videojs from 'video.js'
import throttle from 'lodash/throttle'
import { registerPluginOnce } from '../utils'

const Plugin = videojs.getPlugin('plugin')

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBCustomEvents extends Plugin {
  /**
   * Create a NFBCustomEvents plugin instance.
   * It will trigger custom events :
   *   - autoplay
   *   - progress25
   *   - progress50
   *   - progress75
   *   - progress95
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.initDuration = this.initDuration.bind(this)
    this.initFirstPlay = this.initFirstPlay.bind(this)
    this.onTimeUpdate = throttle(this.onTimeUpdate.bind(this), 500)

    this.totalDuration = 0
    this.currentPercent = 0
    this.state = {
      firstPlay: false,
      progress25: false,
      progress50: false,
      progress75: false,
      progress95: false
    }

    this.player.ready(this.initFirstPlay)
    // wait data to be loaded for duration
    this.player.on('loadeddata', this.initDuration)
  }

  initFirstPlay () {
    this.log('Init First Play')
    if (this.player.options().autoplay) {
      this.onAutoPlay()
    }
  }

  initDuration () {
    this.log('Init Duration here')
    this.totalDuration = this.player.duration().toFixed(0)
    this.player.on('timeupdate', this.onTimeUpdate)
  }

  onTimeUpdate () {
    // check for currentTime if player was destroy and last throttle
    if (this.player?.currentTime()) {
      this.currentPercent = this.player.currentTime().toFixed(0) / this.totalDuration
      if (this.currentPercent >= 0.25 && !this.state.progress25) {
        this.onProgress(25)
      } else if (this.currentPercent >= 0.5 && !this.state.progress50) {
        this.onProgress(50)
      } else if (this.currentPercent >= 0.75 && !this.state.progress75) {
        this.onProgress(75)
      } else if (this.currentPercent >= 0.95 && !this.state.progress95) {
        this.onProgress(95)
      }
    }
  }

  onAutoPlay () {
    this.log('On Auto Play')
    this.player.trigger('autoplay')
  }

  onProgress (progress) {
    this.log(`Progress: ${progress}`)
    this.state[`progress${progress}`] = true
    this.player.trigger(`progress${progress}`)
  }

  dispose () {
    this.player.off('timeupdate', this.onTimeUpdate)
    super.dispose()
  }
}

// Define default values for the plugin's `state` object here.
NFBCustomEvents.defaultState = {}

// Include the version number.
NFBCustomEvents.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbCustomEvents', NFBCustomEvents)

export default NFBCustomEvents
